// Sale time normalisation (ported from VACWAYlockers, same rules).
// - VACWAYone sales are kiosk sales posted through the Lambda: TimeStamp is the machine's local wall clock
//   labelled as UTC; createdAt (and the ObjectId) is the real UTC upload instant. Legacy sales posted to the
//   Express API have no createdAt. The server-created branches below are kept for parity with lockers.
// - The machine's local zone is its Contract.TimeZone (sale Contract first, then the machine's current one); it is
//   used to display the local hour. The kiosk UTC instant comes from its own clock offset when it can be measured.
// - Older server sales were saved with hard-coded +1h/+2h/+4h shifts; for those the Mongo _id creation
//   instant (set by the server when saving) is the real time.

const DEFAULT_TZ = 'Europe/Madrid';
const TOLERANCE_MS = 2 * 60 * 1000;

// Real insert instant, from the ObjectId's first 4 bytes
export const objectIdDate = (id) => {
  const hex = String(id || '').slice(0, 8);
  return /^[0-9a-f]{8}$/i.test(hex) ? new Date(parseInt(hex, 16) * 1000) : null;
};

const isNear = (a, b) => a && b && Math.abs(a.getTime() - b.getTime()) < TOLERANCE_MS;

// Offset (ms) of a time zone from UTC at a given instant
const tzOffsetMs = (date, timeZone) => {
  const parts = {};
  new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    .formatToParts(date).forEach(p => { parts[p.type] = p.value; });
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
};

const isValidTimeZone = (tz) => {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch (e) { return false; }
};

// IANA zone where the sale's machine is (Contract.TimeZone): sale Contract, then the machine's contract (QR/PDA
// sales store the locker's Machine), then sale.TimeZone (API fallback for old sales without Contract/Machine:
// the customer's contracts zone when they all share one), Europe/Madrid by default.
// Rentals use it too (TimeStamp is real UTC; the API populates Machine.Contract.TimeZone).
export const saleTimeZone = (sale) => {
  const tz = (sale && ((sale.Contract && sale.Contract.TimeZone) || (sale.Machine && sale.Machine.Contract && sale.Machine.Contract.TimeZone) || sale.TimeZone)) || DEFAULT_TZ;
  return isValidTimeZone(tz) ? tz : DEFAULT_TZ;
};

// Short label for a zone: "Europe/Madrid" -> "Madrid"
export const timeZoneLabel = (tz) => String(tz).split('/').pop().replace(/_/g, ' ');

// Reinterpret a wall clock stored as UTC as local time of the given zone -> real UTC instant
export const wallClockToUtc = (d, timeZone = DEFAULT_TZ) => {
  const guess = d.getTime();
  const first = new Date(guess - tzOffsetMs(d, timeZone));
  return new Date(guess - tzOffsetMs(first, timeZone));
};

// Real UTC instant of the payment (TimeStamp)
export const salePaymentDate = (sale) => {
  if (!sale || !sale.TimeStamp) return null;
  const ts = new Date(sale.TimeStamp);
  const inserted = objectIdDate(sale._id);
  if (!inserted) return ts;
  // Real UTC (current server writers)
  if (isNear(ts, inserted)) return ts;
  // Legacy shifted server sale: createdAt shifted too, or QR/PDA -> the insert instant is the payment time
  const created = sale.createdAt ? new Date(sale.createdAt) : null;
  if (['QR', 'PDA'].includes(sale.PaymentMethod) || (created && !isNear(created, inserted))) return inserted;
  // Kiosk: machine's local wall clock labelled as UTC.
  // Uploaded right away -> the kiosk clock offset is read from TimeStamp - createdAt (real UTC), so UTC does not
  // depend on the contract zone being configured; delayed uploads fall back to the contract zone.
  const offset = kioskClockOffsetMs(sale);
  if (offset !== null) return new Date(ts.getTime() - offset);
  return wallClockToUtc(ts, saleTimeZone(sale));
};

const QUARTER_MS = 15 * 60 * 1000;

// Kiosk clock offset from UTC (ms, multiple of 15 min) when the sale was uploaded right away, else null.
// Only trusted within 1h of the contract zone offset (Canary vs Madrid, kiosk on wrong DST...): an upload delayed
// by whole hours would otherwise look like a different clock offset.
export const kioskClockOffsetMs = (sale) => {
  if (!sale || !sale.TimeStamp || !sale.createdAt) return null;
  const ts = new Date(sale.TimeStamp);
  const created = new Date(sale.createdAt);
  const inserted = objectIdDate(sale._id);
  // createdAt must be real UTC (not a legacy shifted server row)
  if (inserted && !isNear(created, inserted)) return null;
  const diff = ts.getTime() - created.getTime(); // = clock offset - upload delay
  const offset = Math.round(diff / QUARTER_MS) * QUARTER_MS;
  const delay = offset - diff;
  if (delay < -60 * 1000 || delay > 90 * 1000) return null;
  if (Math.abs(offset - tzOffsetMs(created, saleTimeZone(sale))) > 3600 * 1000) return null;
  return offset;
};

// True when the kiosk clock offset differs from the contract zone's offset at that instant (wrong Contract.TimeZone)
export const kioskZoneMismatch = (sale) => {
  const offset = kioskClockOffsetMs(sale);
  if (offset === null || offset === 0) return false;
  const paid = new Date(new Date(sale.TimeStamp).getTime() - offset);
  return tzOffsetMs(paid, saleTimeZone(sale)) !== offset;
};

export const formatOffset = (ms) => {
  const sign = ms < 0 ? '-' : '+';
  const m = Math.abs(ms) / 60000;
  return `UTC${sign}${Math.floor(m / 60)}${m % 60 ? ':' + String(m % 60).padStart(2, '0') : ''}`;
};

// Real UTC instant of the upload (createdAt)
export const saleUploadDate = (sale) => {
  if (!sale || !sale.createdAt) return null;
  const created = new Date(sale.createdAt);
  const inserted = objectIdDate(sale._id);
  if (!inserted || isNear(created, inserted)) return created;
  // Legacy shifted createdAt
  return inserted;
};

// ---- Display mode (UTC / Madrid / machine local) for the home hourly chart ----
// Per-viewer preference kept in localStorage; changing it notifies every mounted view.
export const SALE_TIME_MODES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'MADRID', label: 'Madrid' },
  { value: 'LOCAL', label: 'Local' },
];
const MODE_KEY = 'vw.saleTimeMode';
const modeListeners = new Set();
let currentMode = null;

export const getSaleTimeMode = () => {
  if (currentMode) return currentMode;
  let stored = null;
  try { stored = window.localStorage.getItem(MODE_KEY); } catch (e) { stored = null; }
  currentMode = SALE_TIME_MODES.some(m => m.value === stored) ? stored : 'LOCAL';
  return currentMode;
};

export const setSaleTimeMode = (mode) => {
  if (!SALE_TIME_MODES.some(m => m.value === mode) || mode === getSaleTimeMode()) return;
  currentMode = mode;
  try { window.localStorage.setItem(MODE_KEY, mode); } catch (e) { /* private mode: keep in memory */ }
  modeListeners.forEach(fn => fn(mode));
};

export const subscribeSaleTimeMode = (fn) => {
  modeListeners.add(fn);
  return () => modeListeners.delete(fn);
};

// Zone used to display a sale in the given mode
export const displayTimeZone = (sale, mode) => {
  if (mode === 'UTC') return 'UTC';
  if (mode === 'MADRID') return DEFAULT_TZ;
  return saleTimeZone(sale);
};

// Badge text for the given mode ("UTC", "Madrid" or the machine zone city)
export const displayZoneLabel = (sale, mode) => (mode === 'UTC' ? 'UTC' : timeZoneLabel(displayTimeZone(sale, mode)));

// Hour (0-23) of the payment in the given mode (hourly charts). LOCAL = the park's own clock.
export const saleHourIn = (sale, mode = 'LOCAL') => {
  const d = salePaymentDate(sale);
  if (!d || isNaN(d.getTime())) return null;
  const hour = new Intl.DateTimeFormat('en-US', { timeZone: displayTimeZone(sale, mode), hour: '2-digit', hourCycle: 'h23' }).format(d);
  return Number(hour);
};

export const saleLocalHour = (sale) => saleHourIn(sale, 'LOCAL');

export const formatUtc = (d, opts) => d.toLocaleString('es-ES', { timeZone: 'UTC', ...opts });
export const formatInZone = (d, timeZone, opts) => d.toLocaleString('es-ES', { timeZone: timeZone || DEFAULT_TZ, ...opts });
